/**
 * Enhanced i18n Hook with comprehensive language management
 * Ensures proper language switching, fallbacks, and validation
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createLogger } from '@/lib/structuredLogger';

const logger = createLogger('I18n');

type I18nValidatorModule = typeof import('@/lib/i18n-validator');

export type SupportedLanguage = 'en' | 'de' | 'fr' | 'it' | 'nl' | 'es';

interface I18nState {
  currentLanguage: SupportedLanguage;
  isReady: boolean;
  isLoading: boolean;
  error: string | null;
  availableLanguages: SupportedLanguage[];
}

interface I18nActions {
  changeLanguage: (language: SupportedLanguage) => Promise<void>;
  validateTranslations: () => boolean;
  getLanguageInfo: (language: SupportedLanguage) => {
    name: string;
    nativeName: string;
    flag: string;
    isComplete: boolean;
  };
}

const LANGUAGE_INFO: Record<
  SupportedLanguage,
  {
    name: string;
    nativeName: string;
    flag: string;
  }
> = {
  en: { name: 'English', nativeName: 'English', flag: '🇺🇸' },
  de: { name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  fr: { name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  it: { name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  nl: { name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱' },
  es: { name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
};

export const useI18n = (): I18nState & I18nActions => {
  const { i18n } = useTranslation();
  const [state, setState] = useState<I18nState>({
    currentLanguage: i18n.language as SupportedLanguage,
    isReady: i18n.isInitialized,
    isLoading: !i18n.isInitialized,
    error: null,
    availableLanguages: ['en', 'de', 'fr', 'it', 'nl', 'es'],
  });

  const loadValidator = useCallback(async (): Promise<I18nValidatorModule | null> => {
    if (!import.meta.env.DEV) {
      return null;
    }

    return import('@/lib/i18n-validator');
  }, []);

  // Enhanced language change with validation
  const changeLanguage = useCallback(
    async (language: SupportedLanguage) => {
      try {
        setState((prev) => ({ ...prev, isLoading: true, error: null }));

        // Validate the language is supported
        if (!state.availableLanguages.includes(language)) {
          throw new Error(`Language ${language} is not supported`);
        }

        // Change language with proper error handling
        await i18n.changeLanguage(language);

        // Store in localStorage for persistence
        localStorage.setItem('i18nextLng', language);

        // Validate translations for the new language
        const validatorModule = await loadValidator();
        const validation = validatorModule?.i18nValidator.validateAnamnesebogen();
        if (validation && !validation.isValid) {
          logger.warn('Translation validation failed', {
            language,
            errors: validation.errors,
          });
        }

        setState((prev) => ({
          ...prev,
          currentLanguage: language,
          isReady: true,
          isLoading: false,
          error: null,
        }));

        // Force re-render of components that depend on language
        window.dispatchEvent(
          new CustomEvent('languageChanged', {
            detail: { language, timestamp: Date.now() },
          }),
        );
      } catch (error) {
        logger.error('Failed to change language', { error });
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to change language',
        }));
      }
    },
    [i18n, loadValidator, state.availableLanguages],
  );

  // Validate all translations
  const validateTranslations = useCallback((): boolean => {
    // Locale schema validation is a development-time aid and should not pin
    // every locale JSON into the production client bundle.
    return true;
  }, []);

  // Get language information
  const getLanguageInfo = useCallback((language: SupportedLanguage) => {
    const info = LANGUAGE_INFO[language];

    return {
      ...info,
      isComplete: true,
    };
  }, []);

  // Initialize language from localStorage or browser
  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        const storedLanguage = localStorage.getItem('i18nextLng') as SupportedLanguage;
        const browserLanguage = navigator.language.split('-')[0] as SupportedLanguage;

        // Determine initial language
        let initialLanguage: SupportedLanguage = 'en';

        if (storedLanguage && state.availableLanguages.includes(storedLanguage)) {
          initialLanguage = storedLanguage;
        } else if (state.availableLanguages.includes(browserLanguage)) {
          initialLanguage = browserLanguage;
        }

        // Only change if different from current
        if (initialLanguage !== i18n.language) {
          await changeLanguage(initialLanguage);
        } else {
          setState((prev) => ({
            ...prev,
            isReady: true,
            isLoading: false,
          }));
        }
      } catch (error) {
        logger.error('Failed to initialize language', { error });
        setState((prev) => ({
          ...prev,
          isReady: true,
          isLoading: false,
          error: 'Failed to initialize language',
        }));
      }
    };

    if (!i18n.isInitialized) {
      initializeLanguage();
    }
  }, [i18n, changeLanguage, state.availableLanguages]);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setState((prev) => ({
        ...prev,
        currentLanguage: lng as SupportedLanguage,
        isReady: true,
        isLoading: false,
      }));
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // Listen for custom language change events
  useEffect(() => {
    const handleCustomLanguageChange = (event: CustomEvent) => {
      const { language } = event.detail;
      setState((prev) => ({
        ...prev,
        currentLanguage: language,
        isReady: true,
        isLoading: false,
      }));
    };

    window.addEventListener('languageChanged', handleCustomLanguageChange as EventListener);

    return () => {
      window.removeEventListener('languageChanged', handleCustomLanguageChange as EventListener);
    };
  }, []);

  return {
    ...state,
    changeLanguage,
    validateTranslations,
    getLanguageInfo,
  };
};

// Enhanced translation hook with fallbacks
export const useTranslationWithFallback = (namespace?: string) => {
  const { t, i18n } = useTranslation(namespace);
  const { currentLanguage, isReady } = useI18n();

  const tWithFallback = useCallback(
    (key: string, options?: Record<string, unknown>): string => {
      if (!isReady) {
        return key; // Return key if not ready
      }

      const translation = t(key, options);

      // Ensure we always return a string
      if (typeof translation === 'string') {
        return translation;
      }

      // Convert to string and check if translation is missing or same as key
      const translationStr = String(translation);
      if (translationStr === key || !translationStr || translationStr === '[object Object]') {
        logger.warn('Missing translation for key', { key, language: currentLanguage });

        // Try English fallback
        if (currentLanguage !== 'en') {
          const fallbackTranslation = t(key, { ...options, lng: 'en' });
          const fallbackStr = String(fallbackTranslation);
          if (
            typeof fallbackTranslation === 'string' &&
            fallbackStr !== key &&
            fallbackStr !== '[object Object]'
          ) {
            return fallbackStr;
          }
        }

        // Return key as last resort
        return key;
      }

      // If translation is not a string, convert it
      return String(translation);
    },
    [t, currentLanguage, isReady],
  );

  return {
    t: tWithFallback,
    i18n,
    currentLanguage,
    isReady,
  };
};
